/* global Stripe */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import SuccessModal from '../components/SuccessModal';
import HairstylistCard from '../components/HairstylistCard';
import AuthModal from '../components/AuthModal';
import styles from './PaymentPage.module.css';
import { db, storage, auth } from '../firebase';
import { useSignup } from '../contexts/SignupContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { collection, addDoc, doc, setDoc, getDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { generateUniqueIdentificationCode } from '../utils/identificationCode';
import { hasKidsServicesFromCodes } from '../utils/kidsServices';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';

// URL de la Cloud Function Stripe (à adapter si tu changes de région)
const CREATE_CHECKOUT_SESSION_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/createCheckoutSession';

// Clé publique Stripe (à remplacer par ta clé réelle, ou à lire depuis REACT_APP_STRIPE_PK)
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_REPLACE_ME';

function PaymentPage() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [profileCountry, setProfileCountry] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const { createdProfileId, clearSignupData, setProfileId } = useSignup();
  const { currentUser } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Vérifier si l'utilisateur a un profil pending (pour redirection depuis HairstylistPage)
  useEffect(() => {
    const checkPendingProfile = async () => {
      if (createdProfileId) return; // Déjà un profil en cours
      
      if (currentUser) {
        try {
          const q = query(
            collection(db, 'hairstylists'),
            where('userId', '==', currentUser.uid),
            where('paymentStatus', '==', 'pending')
          );
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const profile = snapshot.docs[0];
            // Venant de l'email (pas de createdProfileId) : rediriger vers le profil pour voir la bannière rouge
            navigate(`/provider/hair/${profile.id}`);
            return;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error checking pending profile:', error);
        }
      }
    };
    
    checkPendingProfile();
  }, [currentUser, createdProfileId, navigate]);

  // Récupérer le pays et les données du profil pour adapter l'UI
  useEffect(() => {
    const fetchProfileData = async () => {
      if (createdProfileId) {
        try {
          const profileDoc = await getDoc(doc(db, 'hairstylists', createdProfileId));
          if (profileDoc.exists()) {
            const data = profileDoc.data();
            setProfileCountry(data.country);
            setProfileData(data);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching profile data:', error);
        }
      }
    };
    fetchProfileData();
  }, [createdProfileId]);

  // Catalogue services/catégories pour afficher les tags sur la vignette
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const [categoriesSnap, servicesSnap] = await Promise.all([
          getDocs(query(collection(db, 'categories'), where('type', '==', DEFAULT_PROVIDER_TYPE))),
          getDocs(query(collection(db, 'services'), where('type', '==', DEFAULT_PROVIDER_TYPE))),
        ]);
        setCategories(
          categoriesSnap.docs
            .map((d) => d.data())
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );
        setServices(
          servicesSnap.docs
            .map((d) => {
              const data = d.data();
              return {
                ...data,
                code: data.code || data.prestationCode || '',
                label: data.label || data.prestation || '',
              };
            })
            .filter((s) => s.code && s.label)
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load catalog for payment preview', err);
      }
    };
    fetchCatalog();
  }, []);

  const serviceLabelLookup = useMemo(() => {
    return services.reduce((acc, service) => {
      const code = service.code;
      const frenchLabel = service.label || '';
      const label = language && language !== 'fr' && service.en
        ? service.en
        : (t(`services.${code}`, { defaultValue: frenchLabel }) || frenchLabel);
      acc[code] = label;
      return acc;
    }, {});
  }, [services, t, language]);

  const serviceCategoryLookup = useMemo(() => {
    return services.reduce((acc, service) => {
      const categoryCode = service.categoryCode || '';
      const category = categories.find((cat) => cat.categoryCode === categoryCode);
      const frenchCategoryLabel = category?.label || service.category || service.categoryLabel || categoryCode || t('search.otherServices');
      const categoryLabel = language && language !== 'fr' && category?.en
        ? category.en
        : (t(`categories.${categoryCode}`, { defaultValue: frenchCategoryLabel }) || frenchCategoryLabel);
      acc[service.code] = { label: categoryLabel, code: categoryCode || categoryLabel };
      return acc;
    }, [services, categories, t, language]);
  }, [services, categories, t, language]);

  // Pas de redirection automatique !
  // Si l'utilisateur est connecté OU si on a un profileId, c'est bon
  // Ne rien faire - la page va s'afficher normalement

  const handleStripeCheckout = async () => {
    if (!createdProfileId) {
      alert(t('payment.errorProfileNotFound'));
      navigate(`/signup/${DEFAULT_PROVIDER_TYPE}`);
      return;
    }

    if (!currentUser?.email) {
      alert(t('payment.errorUserNotConnected'));
      return;
    }

    const customerEmail = currentUser.email;

    if (!STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY === 'pk_test_REPLACE_ME') {
      // eslint-disable-next-line no-console
      console.error('[PaymentPage] STRIPE_PUBLISHABLE_KEY n’est pas configurée.');
      alert(t('payment.errorStripeNotConfigured'));
      return;
    }

    // Attendre que Stripe soit disponible avec plusieurs tentatives
    let stripeAvailable = typeof window !== 'undefined' && typeof window.Stripe !== 'undefined';
    if (!stripeAvailable) {
      // Essayer de charger le script si ce n'est pas déjà fait
      const scriptExists = document.querySelector('script[src="https://js.stripe.com/v3"]');
      if (!scriptExists) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3';
        script.async = true;
        document.head.appendChild(script);
        
        // Attendre que le script soit chargé
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(() => reject(new Error('Timeout loading Stripe')), 10000);
        });
      } else {
        // Le script existe, attendre qu'il soit chargé
        let attempts = 0;
        while (typeof window.Stripe === 'undefined' && attempts < 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
      }
      
      stripeAvailable = typeof window !== 'undefined' && typeof window.Stripe !== 'undefined';
    }

    if (!stripeAvailable || typeof window.Stripe === 'undefined') {
      // eslint-disable-next-line no-console
      console.error('[PaymentPage] Stripe.js n’est pas chargé (window.Stripe indisponible).');
      alert(t('payment.errorStripeInit'));
      return;
    }

    setLoading(true);
    try {
      // 1. Créer une session Checkout via la Cloud Function
      const priceId = process.env.REACT_APP_STRIPE_PRICE_ID || 'price_REPLACE_ME';

      const response = await fetch(CREATE_CHECKOUT_SESSION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          customerEmail,
          existingProfileId: createdProfileId, // Envoyer l'ID du profil créé pour l'upgrade
        }),
      });

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.error('[PaymentPage] createCheckoutSession failed', await response.text());
        alert(t('payment.errorCheckout'));
        setLoading(false);
        return;
      }

      const data = await response.json();
      const { sessionId } = data;

      if (!sessionId) {
        // eslint-disable-next-line no-console
        console.error('[PaymentPage] Réponse sans sessionId', data);
        alert("Erreur lors de la création de la session de paiement (sessionId manquant).");
        setLoading(false);
        return;
      }

      // 2. Initialiser Stripe et rediriger vers Checkout
      const stripe = window.Stripe(STRIPE_PUBLISHABLE_KEY);
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('[PaymentPage] redirectToCheckout error', error);
        alert(t('payment.errorRedirectStripe'));
        setLoading(false);
      }
      // Sinon, le navigateur est redirigé vers Stripe, puis vers success_url / cancel_url
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating checkout session:', error);
      alert(t('payment.errorCheckout'));
      setLoading(false);
    }
  };

  const handleSkipUpgrade = () => {
    // Nettoyer le contexte et rediriger vers l'accueil
    clearSignupData();
    navigate('/');
  };

  const handleUpgradeToPro = async () => {
    // Simplement lancer le processus de paiement Stripe
    await handleStripeCheckout();
  };

  const isGermanLayout = profileCountry && profileCountry !== 'BE';

  // Utilisateur non connecté : demander de se connecter pour activer le profil
  if (!currentUser && !createdProfileId) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.signInPrompt}>
            <h1 className={styles.signInTitle}>{t('payment.signInToActivate')}</h1>
            <p className={styles.signInText}>{t('payment.signInToActivateText')}</p>
            <button
              type="button"
              className={styles.signInButton}
              onClick={() => setShowAuthModal(true)}
            >
              {t('payment.signIn')}
            </button>
          </div>
        </div>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {!isGermanLayout && <ProgressBar currentStep={3} />}

        {isGermanLayout ? (
          /* Premium paywall layout: white core, 65/35 grid, sticky CTA on mobile */
          <section className={styles.paywall}>
            <div className={styles.paywallCore}>
              <div className={styles.paywallStepper}>
                <ProgressBar currentStep={3} />
              </div>

              <header className={styles.paywallHero}>
                <h1 className={styles.paywallHeadline}>{t('payment.germanReadyTitle')}</h1>
                <p className={styles.paywallSubhead}>{t('payment.germanReadySubtitle')}</p>
              </header>

              <div className={styles.paywallGrid}>
                {/* Left: PRO offer card dans conteneur bordure noire (sticky on desktop) */}
                <div className={styles.paywallOfferWrap}>
                  <div className={styles.paywallOfferBorder}>
                  <div className={styles.paywallOfferCard}>
                    <div className={styles.paywallPriceBlock}>
                      <span className={styles.paywallPrice}>9,99 €</span>
                      <span className={styles.paywallPriceUnit}>{t('payment.perMonth')}</span>
                    </div>
                    <h2 className={styles.paywallOfferTitle}>{t('payment.germanProVisibility')}</h2>
                    <p className={styles.paywallMicrocopy}>{t('payment.paywallMicrocopy')}</p>
                    <ul className={styles.paywallBenefits}>
                      <li>
                        <svg className={styles.paywallCheck} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="10" fill="#B19CD9"/>
                          <path d="M6 10l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t('payment.germanBenefitMap')}</span>
                      </li>
                      <li>
                        <svg className={styles.paywallCheck} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="10" fill="#B19CD9"/>
                          <path d="M6 10l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t('payment.germanBenefitSearch')}</span>
                      </li>
                      <li>
                        <svg className={styles.paywallCheck} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="10" fill="#B19CD9"/>
                          <path d="M6 10l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t('payment.germanBenefitPhotos')}</span>
                      </li>
                      <li>
                        <svg className={styles.paywallCheck} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="10" fill="#B19CD9"/>
                          <path d="M6 10l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t('payment.germanBenefitStats')}</span>
                      </li>
                    </ul>
                    <button
                      type="button"
                      className={styles.paywallCta}
                      onClick={handleUpgradeToPro}
                      disabled={loading}
                    >
                      {loading ? t('payment.redirecting') : t('payment.paywallCtaGoLive')}
                    </button>
                    <p className={styles.paywallCtaNote}>{t('payment.germanActivateNote')}</p>
                  </div>
                  </div>
                </div>

                {/* Right: preview = composant vignette page recherche */}
                <div className={styles.paywallPreviewWrap}>
                  <p className={styles.paywallPreviewLabel}>{t('payment.paywallPreviewLabel')}</p>
                  {profileData && (
                    <div className={styles.paywallPreviewHalo}>
                    <HairstylistCard
                      stylist={{
                        id: createdProfileId,
                        ...profileData,
                        accountType: 'PRO'
                      }}
                      serviceLookup={serviceLabelLookup}
                      serviceCategoryLookup={serviceCategoryLookup}
                    />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile: sticky bottom CTA */}
            <div className={styles.paywallStickyCta}>
              <button
                type="button"
                className={styles.paywallCta}
                onClick={handleUpgradeToPro}
                disabled={loading}
              >
                {loading ? t('payment.redirecting') : t('payment.paywallCtaGoLive')}
              </button>
            </div>
          </section>
        ) : (
          /* Layout classique (autres pays) */
          <section className={styles.successSection}>
            <div className={styles.successIconCircle}>
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#4CAF50" />
                <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h1 className={styles.successTitle}>{t('payment.successAccountCreated')}</h1>

            {profileData && (
              <div className={styles.profilePreview}>
                <HairstylistCard
                  stylist={{
                    id: createdProfileId,
                    ...profileData,
                    accountType: 'PRO'
                  }}
                  serviceLookup={serviceLabelLookup}
                  serviceCategoryLookup={serviceCategoryLookup}
                />
              </div>
            )}

            <p className={styles.successSubtitle}>{t('payment.successSubtitle')}</p>
            <p className={styles.successText}>{t('payment.successProIntro')}</p>

            <div className={styles.proCard}>
              <h2 className={styles.proCardTitle}>{t('payment.proCardTitle')}</h2>
              <p className={styles.proCardSubtext}>{t('payment.proCardSubtext')}</p>

              <ul className={styles.proFeaturesList}>
                <li className={styles.proFeatureItem}>
                  <svg className={styles.checkIconPurple} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#B19CD9" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('payment.proFeatureVerifiedBadge')}</span>
                </li>
                <li className={styles.proFeatureItem}>
                  <svg className={styles.checkIconPurple} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#B19CD9" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('payment.proFeatureSearchable')}</span>
                </li>
                <li className={styles.proFeatureItem}>
                  <svg className={styles.checkIconPurple} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#B19CD9" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('payment.proFeatureGallery')}</span>
                </li>
                <li className={styles.proFeatureItem}>
                  <svg className={styles.checkIconPurple} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#B19CD9" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('payment.proFeatureReviews')}</span>
                </li>
                <li className={styles.proFeatureItem}>
                  <svg className={styles.checkIconPurple} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#B19CD9" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('payment.proFeatureDashboard')}</span>
                </li>
                <li className={styles.proFeatureItem}>
                  <svg className={styles.checkIconPurple} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#B19CD9" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('payment.proFeatureServices')}</span>
                </li>
                <li className={styles.proFeatureItem}>
                  <svg className={styles.checkIconPurple} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#B19CD9" />
                    <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('payment.proFeatureCancel')}</span>
                </li>
              </ul>

              <button
                type="button"
                className={styles.proButton}
                onClick={handleUpgradeToPro}
                disabled={loading}
              >
                {loading ? t('payment.redirecting') : t('payment.goProButton')}
              </button>
              <p className={styles.securityText}>{t('payment.securityText')}</p>
            </div>

            <button
              type="button"
              className={styles.skipButton}
              onClick={handleSkipUpgrade}
            >
              {t('payment.skipUpgrade')}
            </button>
          </section>
        )}
      </div>
    </div>
  );
}

export default PaymentPage;

